import { useCallback, useState } from 'react';
import type { Validate } from 'react-hook-form';

import type { PhoneType } from '~entities/person';

import isEmpty from '@tinkoff/utils/is/empty';
import isString from '@tinkoff/utils/is/string';

type PhoneValues = Record<PhoneType, string | undefined>;

const phoneInUse = (
  currentType: PhoneType,
  phones: PhoneValues
): Validate<unknown> => {
  return function phoneInUseValidator(currentPhone) {
    if (isEmpty(currentPhone) || !isString(currentPhone)) {
      return undefined;
    }

    const phoneExists = Object.entries(phones).some(
      ([type, phone]) => phone === currentPhone && type !== currentType
    );

    return phoneExists ? 'This phone number is already in use' : undefined;
  };
};

export function usePhonesUnicityValidation(initialValue: PhoneValues) {
  const [phones, setPhones] = useState<PhoneValues>(initialValue);

  const handlePhoneNumberUpdate = useCallback(
    (value: string | undefined, type: PhoneType) => {
      if (phones[type] !== value) {
        setPhones((prevPhones) => ({ ...prevPhones, [type]: value }));
      }
    },
    [phones]
  );

  const validatePhone = useCallback(
    (type: PhoneType) => phoneInUse(type, phones),
    [phones]
  );

  return { handlePhoneUpdate: handlePhoneNumberUpdate, validatePhone };
}
